import { POST_LIMITS } from './constants';
import { isValidGeohash } from './geo';
import type { CreatePostInput, PostImage } from './types';

export type Validation<T> = { ok: true; value: T } | { ok: false; field: string; error: string };

// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g;

/** Normalizes newlines, strips control characters (keeping \n and \t) and trims. */
export function cleanText(s: string): string {
  return s.replace(/\r\n?/g, '\n').replace(CONTROL_CHARS, '').trim();
}

/** Length in user-perceived characters (code points), so emoji count as one. */
export function charLength(s: string): number {
  return [...s].length;
}

/** First line-ish of a description, cut at a word boundary, for tiles. */
export function excerptOf(body: string, max: number): string {
  const flat = body.replace(/\s+/g, ' ').trim();
  if ([...flat].length <= max) return flat;
  const cut = [...flat].slice(0, max).join('');
  const space = cut.lastIndexOf(' ');
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[\s.,;:!?-]+$/, '')}…`;
}

/** Returns a canonical http(s) URL, or null when the link isn't acceptable. */
export function normalizeLink(raw: string): string | null {
  const s = raw.trim();
  if (!s || s.length > POST_LIMITS.linkMaxLength) return null;
  let url: URL;
  try {
    url = new URL(/^[a-z][a-z0-9+.-]*:/i.test(s) ? s : `https://${s}`);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  if (!url.hostname.includes('.') || url.username || url.password) return null;
  return url.toString();
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Image objects must live under the uploader's own folder:
 * uploads/{uid}/{postKey}/{index}-{full|thumb}.{webp|jpg}
 */
export function imagePathPattern(uid: string): RegExp {
  return new RegExp(
    `^uploads/${escapeRe(uid)}/([A-Za-z0-9_-]{8,64})/([0-3])-(full|thumb)\\.(webp|jpg)$`,
  );
}

const fail = (field: string, error: string): Validation<never> => ({ ok: false, field, error });

function validateImages(raw: unknown, uid: string): Validation<PostImage[]> {
  if (raw === undefined) return { ok: true, value: [] };
  if (!Array.isArray(raw)) return fail('images', 'Images must be a list.');
  if (raw.length > POST_LIMITS.imagesMax)
    return fail('images', `At most ${POST_LIMITS.imagesMax} images.`);
  const re = imagePathPattern(uid);
  let postKey: string | null = null;
  const out: PostImage[] = [];
  for (const [i, img] of raw.entries()) {
    if (typeof img !== 'object' || img === null) return fail('images', 'Invalid image.');
    const { full, thumb, w, h } = img as Record<string, unknown>;
    const mFull = typeof full === 'string' ? re.exec(full) : null;
    const mThumb = typeof thumb === 'string' ? re.exec(thumb) : null;
    if (!mFull || !mThumb || mFull[3] !== 'full' || mThumb[3] !== 'thumb')
      return fail('images', 'Invalid image path.');
    if (mFull[1] !== mThumb[1] || mFull[2] !== String(i) || mThumb[2] !== String(i))
      return fail('images', 'Invalid image path.');
    if (postKey !== null && mFull[1] !== postKey) return fail('images', 'Invalid image path.');
    postKey = mFull[1]!;
    const dimOk = (v: unknown) =>
      Number.isInteger(v) && (v as number) > 0 && (v as number) <= POST_LIMITS.imageMaxDimension;
    if (!dimOk(w) || !dimOk(h)) return fail('images', 'Invalid image size.');
    out.push({ full: full as string, thumb: thumb as string, w: w as number, h: h as number });
  }
  return { ok: true, value: out };
}

/** Validates and normalizes `createPost` input. Shared by the compose form and the function. */
export function validateCreatePost(input: unknown, uid: string): Validation<CreatePostInput> {
  if (typeof input !== 'object' || input === null) return fail('input', 'Invalid request.');
  const raw = input as Record<string, unknown>;

  if (typeof raw.title !== 'string') return fail('title', 'Add a title.');
  const title = cleanText(raw.title).replace(/\s+/g, ' ');
  if (!title) return fail('title', 'Add a title.');
  if (charLength(title) > POST_LIMITS.titleMax)
    return fail('title', `Keep the title under ${POST_LIMITS.titleMax} characters.`);

  if (raw.body !== undefined && typeof raw.body !== 'string')
    return fail('body', 'Invalid description.');
  const body = cleanText((raw.body as string | undefined) ?? '').replace(/\n{3,}/g, '\n\n');
  // The board shows only titles: the description is what people open a post to read.
  if (!body) return fail('body', 'Add a description: it’s what people open your post to read.');
  if (charLength(body) > POST_LIMITS.bodyMax)
    return fail('body', `Keep the description under ${POST_LIMITS.bodyMax} characters.`);

  if (raw.links !== undefined && !Array.isArray(raw.links)) return fail('links', 'Invalid links.');
  const rawLinks = ((raw.links as unknown[] | undefined) ?? []).filter(
    (l) => !(typeof l === 'string' && l.trim() === ''),
  );
  if (rawLinks.length > POST_LIMITS.linksMax)
    return fail(
      'links',
      POST_LIMITS.linksMax === 1
        ? 'Add one link at most.'
        : `At most ${POST_LIMITS.linksMax} links.`,
    );
  const links: string[] = [];
  for (const l of rawLinks) {
    const url = typeof l === 'string' ? normalizeLink(l) : null;
    if (!url) return fail('links', `Not a valid web link: ${String(l).slice(0, 80)}`);
    if (!links.includes(url)) links.push(url);
  }

  const images = validateImages(raw.images, uid);
  if (!images.ok) return images;

  const g5 = raw.g5 ?? null;
  if (g5 !== null && !isValidGeohash(g5, 5)) return fail('g5', 'Invalid location.');

  return { ok: true, value: { title, body, links, images: images.value, g5 } };
}
